import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SystemStatus = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [followupStats, setFollowupStats] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, followupRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/followups/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setFollowupStats(followupRes.data);
    } catch (error) {
      console.error('Failed to fetch status data:', error);
    }
  };

  const attentionItems = [
    {
      title: 'Critical Follow-Ups',
      count: followupStats?.critical_open || 0,
      icon: AlertCircle,
      color: 'text-red-600 bg-red-50',
      action: () => navigate('/admin/followups?priority=Critical')
    },
    {
      title: 'Overdue Tasks',
      count: followupStats?.overdue || 0,
      icon: Clock,
      color: 'text-orange-600 bg-orange-50',
      action: () => navigate('/admin/followups?overdue=true')
    },
    {
      title: 'Pending Operations',
      count: stats?.pending_operations || 0,
      icon: AlertTriangle,
      color: 'text-yellow-600 bg-yellow-50',
      action: () => navigate('/admin/evaluations?forward_to_operations=true')
    },
    {
      title: 'Open Work Orders',
      count: stats?.open_work_orders || 0,
      icon: CheckCircle2,
      color: 'text-blue-600 bg-blue-50',
      action: () => navigate('/admin/work-orders?status=Open')
    }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">System Status</h2>
        <p className="text-gray-600 mt-1">Items requiring attention</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {attentionItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <Card
              key={index}
              className="cursor-pointer hover:shadow-lg transition-shadow duration-200"
              onClick={item.action}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      {item.title}
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {item.count}
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${item.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Follow-Up Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Tasks</span>
                <span className="font-semibold">{followupStats?.total || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Open</span>
                <span className="font-semibold text-blue-600">{followupStats?.open || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">In Progress</span>
                <span className="font-semibold text-yellow-600">{followupStats?.in_progress || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completed</span>
                <span className="font-semibold text-green-600">{followupStats?.completed || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Vehicle Inspections</span>
                <span className="font-semibold">{stats?.vehicle_inspections || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Work Orders</span>
                <span className="font-semibold">{stats?.work_orders || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Customer Feedback</span>
                <span className="font-semibold">{stats?.customer_feedback || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Avg Rating</span>
                <span className="font-semibold text-green-600">{stats?.avg_rating || 0} ⭐</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SystemStatus;
